import React, { useEffect, useMemo, useState } from 'react';
import { FiUserX } from 'react-icons/fi';
import QuickFilters, {
  CONTACT_QUICK_FILTERS,
  DEAL_QUICK_FILTERS,
} from '../../../components/CRM/Filters/QuickFilters';

/**
 * Multiselect preset chips for the workbench. Mounts the QuickFilters presets
 * plus a custom "No deals yet" chip — the 68%-of-contacts segment that is
 * invisible from the deals page and started this whole project.
 *
 * Chips COMBINE:
 *  - Same field + same list operator → values union (Borrowers + Realtors =
 *    either type, matching how contains_any/in OR their values in Postgres)
 *  - Different fields → conditions AND (Borrowers + Has Deals = both)
 *  - Contradictory chips on the same field (Has Deals vs No deals yet) swap —
 *    selecting one deselects the other instead of ANDing to zero results
 *
 * The merged set REPLACES the table's filters. Editing filters in the toolbar
 * (or applying a smart list) takes over and deactivates all chips.
 */

const NO_DEALS_CHIP = {
  id: 'no-deals-yet',
  label: 'No deals yet',
  icon: FiUserX,
  colorScheme: 'purple',
  filters: [{ field: 'totalDeals', operator: 'eq', value: 0 }],
};

// Operators whose array values can be unioned without changing semantics
// (both OR their values within the field)
const MERGEABLE_OPERATORS = ['contains_any', 'in'];

const stripIds = (filters) =>
  (filters || []).map(({ field, operator, value }) => ({ field, operator, value }));

// Union same field+operator list conditions; everything else stacks (ANDs)
function mergeChipFilters(chips) {
  const merged = [];
  chips.forEach((chip) => {
    (chip.filters || []).forEach(({ field, operator, value }) => {
      const target = merged.find(
        (m) =>
          m.field === field &&
          m.operator === operator &&
          MERGEABLE_OPERATORS.includes(operator) &&
          Array.isArray(m.value) &&
          Array.isArray(value)
      );
      if (target) {
        target.value = [...new Set([...target.value, ...value])];
      } else {
        merged.push({ field, operator, value: Array.isArray(value) ? [...value] : value });
      }
    });
  });
  return merged;
}

// Two chips conflict when they hit the same field in a way that can't merge
function chipsConflict(a, b) {
  return (a.filters || []).some((fa) =>
    (b.filters || []).some(
      (fb) =>
        fa.field === fb.field &&
        !(fa.operator === fb.operator && MERGEABLE_OPERATORS.includes(fa.operator))
    )
  );
}

export default function WorkbenchQuickChips({ entityType, filters, onApply }) {
  const customQuickFilters = useMemo(
    () => (entityType === 'contacts' ? [NO_DEALS_CHIP] : []),
    [entityType]
  );

  const allChips = useMemo(
    () => [
      ...(entityType === 'deals' ? DEAL_QUICK_FILTERS : CONTACT_QUICK_FILTERS),
      ...customQuickFilters,
    ],
    [entityType, customQuickFilters]
  );

  const [selectedIds, setSelectedIds] = useState([]);

  const chipById = (id) => allChips.find((c) => c.id === id);
  const mergedFromSelection = (ids) =>
    mergeChipFilters(ids.map(chipById).filter(Boolean));

  // Toolbar/smart-list edits take over: when the table's filters no longer
  // equal what the selected chips produce, the chips aren't the source of
  // truth anymore — deactivate them (the filters themselves stay).
  useEffect(() => {
    if (selectedIds.length === 0) return;
    const current = JSON.stringify(stripIds(filters));
    const fromChips = JSON.stringify(stripIds(mergedFromSelection(selectedIds)));
    if (current !== fromChips) setSelectedIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Lens/entity change → different chip set entirely
  useEffect(() => {
    setSelectedIds([]);
  }, [entityType]);

  const handleChipClick = (id) => {
    const chip = chipById(id);
    if (!chip) return;

    let next;
    if (selectedIds.includes(id)) {
      next = selectedIds.filter((x) => x !== id);
    } else {
      next = [
        ...selectedIds.filter((selId) => {
          const sel = chipById(selId);
          return sel && !chipsConflict(sel, chip);
        }),
        id,
      ];
    }

    setSelectedIds(next);
    const merged = mergedFromSelection(next);
    onApply(merged.length ? merged : null);
  };

  return (
    <QuickFilters
      entityType={entityType}
      multiSelect
      activeFilterIds={selectedIds}
      customQuickFilters={customQuickFilters}
      onQuickFilterClick={handleChipClick}
    />
  );
}
